// src/SpectatorView.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import LiveScoreDisplay from './LiveScoreDisplay';
import { translatePointCode } from './pointCodeTranslator';
import { analyzePointContext } from './pointContextAnalyzer';  // Analyzer complet
import { useSpeech } from './useSpeech';  // 🆕 Hook synthèse vocale
import ChatPanel from './ChatPanel';  // 🆕 Chat en temps réel
import SpectatorStats from './components/SpectatorStats';  // 🆕 Statistiques en temps réel

function SpectatorView() {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [currentScore, setCurrentScore] = useState(null);
  const [lastPoint, setLastPoint] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [scoreEngineData, setScoreEngineData] = useState(null);  // Données du ScoreEngine
  
  // 🆕 États pour la synthèse vocale
  const { speak, stop, isSupported, isReady, isSpeaking } = useSpeech();
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // 🆕 Refs pour la reconnexion automatique
  const channelRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const isSubscribingRef = useRef(false);
  const lastMessageTimeRef = useRef(Date.now());
  
  // 🆕 Ref pour capturer la valeur actuelle de lastPoint (évite les doublons)
  const lastPointRef = useRef(null);

  // 🆕 État d'affichage connexion avec délai de grâce (évite les flashs orange)
  const [displayConnected, setDisplayConnected] = useState(false);
  const disconnectTimerRef = useRef(null);

  // 🆕 Délai de grâce : ne passer en "déconnecté" qu'après 4 secondes
  useEffect(() => {
    if (isConnected) {
      // Connexion rétablie → annuler le timer et afficher vert immédiatement
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      setDisplayConnected(true);
    } else {
      // Déconnexion → attendre 4 secondes avant d'afficher orange
      disconnectTimerRef.current = setTimeout(() => {
        setDisplayConnected(false);
      }, 4000);
    }
    return () => {
      if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
    };
  }, [isConnected]);

  // Charger les données initiales du match
  useEffect(() => {
    async function loadMatch() {
      try {
        console.log('📡 Chargement du match:', matchId);
        
        const { data, error } = await supabase
          .from('matches')
          .select('*')
          .eq('id', matchId)
          .maybeSingle();
        
        if (error) throw error;
        
        if (!data) {
          setError('Match introuvable ou accès refusé');
          return;
        }
        
        setMatch(data);
        setCurrentScore(data.currentScore || { jeuScore: "0-0", sets: [[0,0]] });
        
        // Charger les points si disponibles
        if (data.engineState?.points && data.engineState.points.length > 0) {
          const allPoints = data.engineState.points;
          
          // ✅ Séparer le dernier point de l'historique
          const lastPointData = allPoints[allPoints.length - 1];
          const historyPoints = allPoints.slice(-5, -1);  // 4 avant-derniers points
          
          // Construire le code du dernier point
          if (lastPointData.codePoint) {
            const lastCode = lastPointData.codePoint.split(';')[0];
            const lastPt = {
              code: lastCode,
              winner: lastPointData.winner,
              timestamp: new Date(),
              count: allPoints.length,
              fullPoint: lastPointData  // 🆕 Stocker le point complet pour l'analyse
            };
            
            setLastPoint(lastPt);
            lastPointRef.current = lastPt;  // 🆕 Initialiser le ref
          }
          
          // Construire l'historique (sans le dernier)
          const formattedHistory = historyPoints.map((point, idx) => {
            let code = '';
            
            // Si codePoint existe, l'utiliser
            if (point.codePoint) {
              code = point.codePoint.split(';')[0];
            }
            // Sinon construire depuis pointData
            else if (point.pointData) {
              const pd = point.pointData;
              if (pd.familleCode) {
                code = pd.familleCode;
                if (pd.sousTypeCode) code += pd.sousTypeCode;
                if (pd.resultatCode === '') code += '';  // Blanc = gagnant
                else if (pd.resultatCode) code += pd.resultatCode;
                if (pd.nbFrappesCode) code += pd.nbFrappesCode;
                if (pd.fauteDirecteCode) code += pd.fauteDirecteCode;
              }
            }
            
            // Calculer le numéro du point (position dans le tableau + 1)
            const pointNumber = allPoints.length - (historyPoints.length - idx);
            
            return {
              code: code || 'N/A',
              winner: point.winner,
              timestamp: point.timestamp || new Date(),
              count: pointNumber,  // 🆕 Ajouter le numéro du point
              fullPoint: point
            };
          });
          
          setPointsHistory(formattedHistory);
        }
        
        console.log('✅ Match chargé:', data);
      } catch (err) {
        console.error('❌ Erreur chargement:', err);
        setError('Erreur lors du chargement du match');
      }
    }
    
    loadMatch();
  }, [matchId]);

  // 🆕 FONCTION DE CONNEXION RÉUTILISABLE avec reconnexion automatique
  const connectToChannel = React.useCallback(() => {
    if (!matchId || isSubscribingRef.current) {
      console.log('⏸️ Connexion ignorée (en cours ou pas de matchId)');
      return;
    }

    isSubscribingRef.current = true;

    // 🆕 Annuler tout timeout de reconnexion en cours AVANT de nettoyer
    if (reconnectTimeoutRef.current) {
      console.log('⏹️ Annulation timeout de reconnexion en cours');
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Nettoyer l'ancien channel si existe
    if (channelRef.current) {
      console.log('🧹 Nettoyage ancien channel');
      try {
        supabase.removeChannel(channelRef.current);
      } catch (err) {
        console.error('⚠️ Erreur nettoyage channel:', err);
      }
      channelRef.current = null;
    }

    console.log('📡 Connexion au channel:', `match:${matchId}`);
    
    const channel = supabase.channel(`match:${matchId}`, {
      config: {
        broadcast: { self: false },
        presence: { key: matchId }
      }
    });
    
    channelRef.current = channel;

    channel
      .on('broadcast', { event: 'score_update' }, (payload) => {
        console.log('🎾 Score reçu:', payload);
        lastMessageTimeRef.current = Date.now(); // 🆕 Mettre à jour heartbeat
        
        const { score, lastPointCode, lastPointWinner, lastPointFull, scoreEngineData, pointsCount } = payload.payload;
        
        setCurrentScore(score);
        setIsConnected(true);
        
        if (scoreEngineData) {
          setScoreEngineData(scoreEngineData);
        }
        
        if (lastPointCode) {
          console.log('📍 Nouveau point reçu:', lastPointCode, 'n°', pointsCount);
          
          const oldPoint = lastPointRef.current;
          
          if (oldPoint && 
              oldPoint.code === lastPointCode && 
              oldPoint.count === pointsCount &&
              !oldPoint.isUndo) {
            console.log('⏭️ Point déjà traité, ignoré');
            return;
          }
          
          const newPoint = {
            code: lastPointCode,
            winner: lastPointWinner,
            timestamp: new Date(),
            count: pointsCount,
            fullPoint: lastPointFull
          };
          
          lastPointRef.current = newPoint;
          setLastPoint(newPoint);
          
          console.log('✅ Dernier point mis à jour:', lastPointCode, 'n°', pointsCount);
          
          if (oldPoint && (oldPoint.count !== pointsCount || oldPoint.isUndo)) {
            console.log('📋 Déplacement ancien lastPoint vers historique:', oldPoint.code, 'n°', oldPoint.count, oldPoint.isUndo ? '(annulation)' : '(point)');
            setPointsHistory(hist => {
              const newHist = [...hist.slice(-4), oldPoint];
              console.log('📋 Historique après ajout:', newHist.map(p => `${p.code}(n°${p.count})${p.isUndo ? '⚠️' : ''}`).join(', '));
              return newHist;
            });
          }
        }
      })
      .on('broadcast', { event: 'point_undo' }, (payload) => {
        console.log('🔙 Annulation de point reçue:', payload);
        lastMessageTimeRef.current = Date.now(); // 🆕 Mettre à jour heartbeat
        
        const { score, scoreEngineData, pointsCount } = payload.payload;
        
        setCurrentScore(score);
        setIsConnected(true);
        
        if (scoreEngineData) {
          setScoreEngineData(scoreEngineData);
        }
        
        const oldPoint = lastPointRef.current;
        
        const undoPoint = {
          code: 'UNDO',
          winner: null,
          timestamp: new Date(),
          count: pointsCount,
          fullPoint: null,
          isUndo: true
        };
        
        lastPointRef.current = undoPoint;
        setLastPoint(undoPoint);
        
        console.log('🔙 Point annulé, nouveau score:', score.jeuScore);
        
        if (oldPoint && !oldPoint.isUndo) {
          console.log('📋 Déplacement ancien lastPoint vers historique (ANNULÉ):', oldPoint.code, 'n°', oldPoint.count);
          setPointsHistory(hist => {
            const newHist = [...hist.slice(-4), { ...oldPoint, isUndo: true }];
            console.log('📋 Historique après annulation:', newHist.map(p => `${p.code}(n°${p.count})${p.isUndo ? '⚠️' : ''}`).join(', '));
            return newHist;
          });
        }
      })
      .subscribe((status) => {
        console.log('📡 Status:', status);
        isSubscribingRef.current = false;
        
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
          setError(null);
          console.log('✅ Connecté au channel avec succès');
        } else if (status === 'CLOSED') {
          setIsConnected(false);
          console.log('❌ Channel fermé, reconnexion dans 2s...');
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('🔄 Tentative de reconnexion...');
            connectToChannel();
          }, 2000);
        } else if (status === 'CHANNEL_ERROR') {
          setIsConnected(false);
          setError('Erreur de connexion');
          console.error('❌ Erreur channel, reconnexion dans 3s...');
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('🔄 Reconnexion après erreur...');
            connectToChannel();
          }, 3000);
        }
      });

  }, [matchId]);

  // S'abonner au broadcast en temps réel avec reconnexion automatique
  useEffect(() => {
    if (!matchId) return;
    
    // Connexion initiale
    connectToChannel();

    // 🆕 HEARTBEAT - Vérifier que la connexion est toujours active
    heartbeatIntervalRef.current = setInterval(() => {
      const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current;
      
      // 🆕 Augmenter le délai à 2 minutes (120s) au lieu de 30s
      // Dans un match de tennis, il peut se passer du temps entre les points
      if (timeSinceLastMessage > 120000) {
        console.log('⚠️ Aucun message depuis 2 minutes, vérification connexion...');
        
        const channelState = channelRef.current?.state;
        console.log('📡 État du channel:', channelState);
        
        // 🆕 NE PAS changer l'état de connexion si le channel est toujours 'joined'
        // Seulement reconnecter si vraiment déconnecté
        if (channelState !== 'joined') {
          console.log('🔄 Channel inactif, reconnexion...');
          setIsConnected(false);
          connectToChannel();
        } else {
          console.log('✅ Channel actif, pas de reconnexion nécessaire');
        }
      }
    }, 30000); // 🆕 Vérifier toutes les 30 secondes (au lieu de 10)

    // 🆕 GESTION DU VISIBILITY CHANGE (retour sur l'app)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👀 App revenue au premier plan, vérification connexion...');
        const channelState = channelRef.current?.state;
        if (channelState !== 'joined') {
          console.log('🔄 Reconnexion nécessaire');
          connectToChannel();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      console.log('🧹 Cleanup SpectatorView');
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (channelRef.current) {
        console.log('📡 Déconnexion du channel');
        try {
          supabase.removeChannel(channelRef.current);
        } catch (err) {
          console.error('⚠️ Erreur déconnexion:', err);
        }
        channelRef.current = null;
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [matchId, connectToChannel]);
  
  // 🆕 LECTURE VOCALE automatique quand nouveau point
  useEffect(() => {
    // Ne rien faire si :
    // - Voix désactivée
    // - Pas de point
    // - Pas d'interaction utilisateur (requis sur mobile)
    if (!voiceEnabled || !lastPoint || !hasUserInteracted || !match) {
      return;
    }
    
    console.log('🔊 Préparation lecture vocale du point', lastPoint.count);
    
    // 🆕 Cas spécial : annulation de point
    if (lastPoint.isUndo) {
      const text = "Point annulé par le noteur";
      console.log('🔊 Lecture vocale ANNULATION:', text);
      speak(text);
      return;
    }
    
    // Cas normal : lecture du point
    const pointText = translatePointCode(lastPoint.code, match, lastPoint.winner);
    
    // 🆕 Ajouter l'analyse du contexte (jeu, set, break, etc.)
    let fullText = pointText;
    
    if (lastPoint.fullPoint) {
      const previousPoint = pointsHistory.length > 0 
        ? pointsHistory[pointsHistory.length - 1].fullPoint 
        : null;
      
      const events = analyzePointContext(
        lastPoint.fullPoint, 
        match, 
        previousPoint, 
        scoreEngineData
      );
      
      if (events.length > 0) {
        fullText = pointText + ". " + events.join(". ");
      }
    }
    
    console.log('🔊 Lecture vocale:', fullText);
    speak(fullText);
  }, [lastPoint, voiceEnabled, hasUserInteracted, match, pointsHistory, scoreEngineData, speak]);

  // 🆕 Gestionnaire d'activation/désactivation de la voix
  const handleVoiceToggle = () => {
    if (!hasUserInteracted) {
      // Premier clic = interaction utilisateur
      setHasUserInteracted(true);
    }
    
    if (!voiceEnabled) {
      // Activation de la voix
      setVoiceEnabled(true);
      console.log('🔊 Synthèse vocale activée');
      
      // Tester immédiatement avec le dernier point
      if (lastPoint && match && !lastPoint.isUndo) {
        const text = translatePointCode(lastPoint.code, match, lastPoint.winner);
        speak(text);
      }
    } else {
      // Désactivation de la voix
      setVoiceEnabled(false);
      stop();
      console.log('🔇 Synthèse vocale désactivée');
    }
  };

  // 🆕 Mémoriser l'analyse du contexte du dernier point pour éviter les recalculs
  const lastPointEvents = useMemo(() => {
    if (!lastPoint?.fullPoint || lastPoint.isUndo || !match) {
      return [];
    }
    
    const previousPoint = pointsHistory.length > 0 
      ? pointsHistory[pointsHistory.length - 1].fullPoint 
      : null;
    
    return analyzePointContext(
      lastPoint.fullPoint, 
      match, 
      previousPoint, 
      scoreEngineData
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lastPoint?.count,  // 🆕 Ne recalculer que si le numéro de point change
    lastPoint?.isUndo,
    match?.id,
    pointsHistory.length,
    scoreEngineData?.points?.length
  ]);

  if (error) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        fontSize: '18px',
        color: '#d32f2f'
      }}>
        {error}
      </div>
    );
  }

  if (!match) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        fontSize: '18px'
      }}>
        <div style={{ 
          display: 'inline-block',
          width: '50px',
          height: '50px',
          border: '5px solid #f3f3f3',
          borderTop: '5px solid #1976D2',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ marginTop: '20px' }}>Chargement du match...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Indicateur de connexion */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto 20px',
        padding: '10px 12px',
        backgroundColor: displayConnected ? 'rgba(76, 175, 80, 0.9)' : 'rgba(200, 130, 0, 0.85)',
        color: 'white',
        borderRadius: '8px',
        textAlign: 'center',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'background-color 0.8s ease',
      }}>
        <span style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: 'white',
          display: 'inline-block',
          flexShrink: 0,
          animation: displayConnected ? 'pulse 2s ease-in-out infinite' : 'spin 1.2s linear infinite',
          opacity: displayConnected ? 1 : 0.8,
        }}></span>
        {displayConnected
          ? '🟢 Connecté au match en direct'
          : '🔄 Reconnexion automatique en cours...'}
      </div>

      {/* Score live */}
      <div style={{ marginBottom: '20px' }}>
        <LiveScoreDisplay match={match} currentScore={currentScore} points={scoreEngineData?.points?.length > 0 ? scoreEngineData.points : (match?.engineState?.points || [])} />
      </div>

      {/* Dernier point */}
      {lastPoint && (
        <div style={{
          maxWidth: '600px',
          margin: '20px auto',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            marginBottom: '8px',
            textTransform: 'uppercase',
            fontWeight: '600'
          }}>
            {lastPoint.isUndo ? '⚠️ Annulation' : `Dernier point (${lastPoint.count})`}
          </div>
          <div style={{ 
            fontSize: '18px', 
            fontWeight: '600',
            color: '#333',
            lineHeight: '1.4',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            {/* 🆕 MODIFIÉ : Affichage uniforme pour annulation */}
            {lastPoint.isUndo ? (
              <>
                <span style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  flexShrink: 0,
                  marginTop: '2px',
                  backgroundColor: '#FF9800'
                }}></span>
                <span style={{ color: '#d32f2f', fontWeight: '700' }}>Point n°{lastPoint.count} annulé par le noteur</span>
              </>
            ) : (
              <>
                {/* Carré de couleur selon le joueur */}
                <span style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  flexShrink: 0,
                  marginTop: '2px',
                  backgroundColor: lastPoint.winner === 'J1' ? '#1976D2' : '#FF9800'
                }}></span>
                <span>Point n°{lastPoint.count} {translatePointCode(lastPoint.code, match, lastPoint.winner)?.replace(/^Point /, '')}</span>
              </>
            )}
          </div>
          
          {/* 🆕 Événements du point (jeu, set, balles de break, etc.) - PAS pour undo */}
          {lastPointEvents.length > 0 && (
            <div style={{ 
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#fff3e0',
              borderRadius: '8px',
              borderLeft: '4px solid #ff9800'
            }}>
              {lastPointEvents.map((event, i) => (
                <div key={i} style={{ 
                  fontSize: '14px', 
                  fontWeight: event.startsWith('   ') ? '400' : '600',
                  color: '#e65100',
                  marginBottom: i < lastPointEvents.length - 1 ? '4px' : '0',
                  paddingLeft: event.startsWith('   ') ? '16px' : '0'
                }}>
                  {event}
                </div>
              ))}
            </div>
          )}
          
          <div style={{ 
            fontSize: '12px', 
            color: '#999',
            marginTop: '8px',
            fontFamily: 'monospace'
          }}>
            {lastPoint.code} • {new Date(lastPoint.timestamp).toLocaleTimeString('fr-FR')}
          </div>
        </div>
      )}

      {/* Historique des points précédents (incluant les annulations) */}
      {pointsHistory.length > 0 && (
        <div style={{
          maxWidth: '600px',
          margin: '20px auto',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            marginBottom: '12px',
            textTransform: 'uppercase',
            fontWeight: '600'
          }}>
            Points précédents
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pointsHistory.slice().reverse().map((point, i) => (
              <div key={i} style={{
                display: 'flex',
                gap: '12px',
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
              }}>
                {/* 🆕 MODIFIÉ : Même style pour annulation */}
                {point.isUndo ? (
                  <>
                    <span style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '3px',
                      flexShrink: 0,
                      marginTop: '2px',
                      backgroundColor: '#FF9800'
                    }}></span>
                    
                    <div style={{ flex: 1 }}>
                      <span style={{ 
                        fontSize: '15px', 
                        fontWeight: '700', 
                        display: 'block', 
                        marginBottom: '4px', 
                        lineHeight: '1.4',
                        color: '#d32f2f'
                      }}>
                        Point n°{point.count} annulé par le noteur
                      </span>
                      <span style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>
                        UNDO • {point.timestamp ? new Date(point.timestamp).toLocaleTimeString('fr-FR') : ''}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Carré de couleur selon le joueur */}
                    <span style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '3px',
                      flexShrink: 0,
                      marginTop: '2px',
                      backgroundColor: point.winner === 'J1' ? '#1976D2' : '#FF9800'
                    }}></span>
                    
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '15px', fontWeight: '600', display: 'block', marginBottom: '4px', lineHeight: '1.4' }}>
                        Point n°{point.count || '?'} {translatePointCode(point.code, match, point.winner)?.replace(/^Point /, '')}
                      </span>
                      <span style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>
                        {point.code} • {point.timestamp ? new Date(point.timestamp).toLocaleTimeString('fr-FR') : ''}
                      </span>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info du match */}
      <div style={{
        maxWidth: '600px',
        margin: '20px auto',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '16px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
          {new Date(match.dateMatch).toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </div>
        {match.tournoiNom && (
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
            {match.tournoiNom}
          </div>
        )}
      </div>

      {/* 🆕 STATISTIQUES EN TEMPS RÉEL */}
      <SpectatorStats 
        match={match} 
        scoreEngineData={scoreEngineData}
        showEffets={true}
      />

      {/* 🆕 Bouton volume — rond, même taille que bouton chat */}
      {isSupported && isReady && (
        <button
          onClick={handleVoiceToggle}
          style={{
            position: 'fixed',
            bottom: '156px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: voiceEnabled
              ? (isSpeaking ? '#FF9800' : '#4caf50')
              : 'rgba(100, 100, 100, 0.85)',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 999,
            transition: 'background-color 0.3s, transform 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title={voiceEnabled ? 'Désactiver lecture vocale' : 'Activer lecture vocale'}
        >
          {isSpeaking ? '🔊' : voiceEnabled ? '🔈' : '🔇'}
        </button>
      )}

      {/* 💬 Chat en temps réel avec le noteur */}
      {match && (
        <ChatPanel 
          matchId={matchId} 
          isScorer={false} 
          match={match}
        />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default SpectatorView;